/**
 * i18n/LanguageContext.js - Contexto de Idioma
 * CG Business ERP
 */

import { createContext, useContext, useEffect, useState } from 'react';
import translations from './translations';
import { authAPI, usersAPI } from '../lib/api';
import { getStoredAuthToken } from '../lib/authStorage';

const LanguageContext = createContext();
const LANGUAGE_STORAGE_KEY = 'erp_language';

export const LANGUAGES = {
  pt: { code: 'pt', name: 'Português', flag: '🇵🇹' },
  en: { code: 'en', name: 'English', flag: '🇬🇧' }
};

export function LanguageProvider({ children }) {
  const getInitialLanguage = () => {
    const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (saved && LANGUAGES[saved]) {
      return saved;
    }

    return 'pt';
  };

  const [language, setLanguageState] = useState(() => {
    return getInitialLanguage();
  });

  const persistLanguageLocally = (lang) => {
    setLanguageState(lang);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  };

  const setLanguage = async (lang, options = {}) => {
    if (!LANGUAGES[lang]) {
      return;
    }

    persistLanguageLocally(lang);

    if (options.skipRemoteSync) {
      return;
    }

    const token = getStoredAuthToken();
    if (!token) {
      return;
    }

    try {
      await usersAPI.updateMyLanguagePreference(lang);
    } catch (error) {
      console.warn('Failed to persist language preference', error);
    }
  };

  useEffect(() => {
    let active = true;

    const syncLanguageFromSession = async () => {
      const token = getStoredAuthToken();
      if (!token) {
        return;
      }

      try {
        const sessionResponse = await authAPI.getSession();
        const sessionLanguage = sessionResponse.data?.user?.preferred_language;
        if (active && sessionLanguage && LANGUAGES[sessionLanguage]) {
          persistLanguageLocally(sessionLanguage);
        }
      } catch {
        // ignore session sync failures and keep local preference
      }
    };

    syncLanguageFromSession();

    return () => {
      active = false;
    };
  }, []);

  const resolveTranslation = (dictionary, keys) => {
    let value = dictionary;

    for (const k of keys) {
      if (value && Object.prototype.hasOwnProperty.call(value, k)) {
        value = value[k];
      } else {
        return undefined;
      }
    }

    return value;
  };

  const interpolate = (value, params = {}) => {
    if (typeof value !== 'string') return value;

    return value.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, paramKey) => {
      if (params[paramKey] === undefined || params[paramKey] === null) {
        return '';
      }

      return String(params[paramKey]);
    });
  };

  const t = (key, params = {}) => {
    const keys = key.split('.');

    let value = resolveTranslation(translations[language], keys);

    if (value === undefined && language !== 'pt') {
      value = resolveTranslation(translations.pt, keys);
    }

    if (value === undefined) {
      console.warn(`Translation missing: ${key}`);
      return key;
    }

    return interpolate(value, params);
  };

  // Shorthand for common translations
  const tNav = (key) => t(`nav.${key}`);
  const tAction = (key) => t(`actions.${key}`);
  const tLabel = (key) => t(`labels.${key}`);
  const tPeriod = (key) => t(`periods.${key}`);
  const tMsg = (key) => t(`messages.${key}`);

  return (
    <LanguageContext.Provider value={{
      language,
      setLanguage,
      t,
      tNav,
      tAction,
      tLabel,
      tPeriod,
      tMsg,
      languages: LANGUAGES,
      isPortuguese: language === 'pt',
      isEnglish: language === 'en'
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
